import { create } from 'zustand'
import {
	createRelayType,
	deleteRelayType,
	fetchRelaysByType,
	fetchRelayTypes,
	updateRelayType,
	createRelay,
	updateRelay,
	deleteRelay,
	createElectric,
	createMechanic,
	deleteElectric,
	deleteMechanic,
	fetchElectricByType,
	fetchMechanicByType,
	updateElectric,
	updateMechanic,
	createRelayOne,
	fetchRelayOnesByRelayId,
	createMechanicValue,
	createElectricValue,
	fetchRelayOneMechanicValue,
	fetchRelayOneElectricValue,
	createRecord,
	fetchRelayOneRecords,
	fetchRelayOneRecordOne,
	checkRecordMechanicValue,
	checkRecordElectricValue,
	getRecordsJournal,
	JournalRecordsResponse
} from '../api/relay'
import {
	ElectricMechanic,
	ElectricMechanicValue,
	Record,
	Relay,
	RelayOne,
	RelayType
} from '../helpers/types/relay'

export interface RelayState {
	relayTypes: RelayType[]

	relayOnes: RelayOne[]

	fetchedRelayOneRelayIds: string[]

	setDataFromUpdate: (
		data:
			| {
				type: 'relayType'
				data: RelayType
				action: 'create' | 'update' | 'delete'
			}
			| {
				type: 'relay'
				data: Relay
				action: 'create' | 'update' | 'delete'
			}
			| {
				type: 'mechanic'
				data: ElectricMechanic
				action: 'create' | 'update' | 'delete'
			}
			| {
				type: 'electric'
				data: ElectricMechanic
				action: 'create' | 'update' | 'delete'
			}
			| {
				type: 'relayOne'
				data: RelayOne
				action: 'create' | 'update' | 'delete'
			}
	) => void

	addRelayType: () => void
	addRelay: (relayTypeId: string) => void
	addRelayOne: (relayTypeId: string, relayId: string) => void
	addMechanic: (relayTypeId: string) => void
	addElectric: (relayTypeId: string) => void

	createRelayType: (relayType: RelayType) => void
	createRelay: (relay: Relay) => void
	createRelayOne: (relay: RelayOne) => Promise<RelayOne>
	createMechanic: (mechanic: ElectricMechanic) => void
	createElectric: (electric: ElectricMechanic) => void
	createRecord: (
		record: Omit<Record, 'mechanicValues' | 'electricValues'>
	) => void
	createMechanicValue: (
		mechanic: ElectricMechanicValue[]
	) => Promise<ElectricMechanicValue[]>
	createElectricValue: (
		electric: ElectricMechanicValue[]
	) => Promise<ElectricMechanicValue[]>

	fetchRelayTypes: () => Promise<void>
	fetchRelaysByType: (relayTypeId: string) => Promise<void>
	fetchRelayOnesByRelayId: (relayId: string) => Promise<void>
	fetchRelayOneRecords: (
		relayTypeId: string,
		relayId: string,
		relayOneId: string,
		page: number
	) => Promise<Record[]>
	fetchRelayOneRecordOne: (
		relayTypeId: string,
		relayId: string,
		relayOneId: string,
		recordId: string
	) => Promise<Record>
	fetchMechanicsByType: (relayTypeId: string) => Promise<void>
	fetchElectricsByType: (relayTypeId: string) => Promise<void>
	fetchRelayOneMechanicValue: (
		relayOneId: string
	) => Promise<ElectricMechanicValue[]>
	fetchRelayOneElectricValue: (
		relayOneId: string
	) => Promise<ElectricMechanicValue[]>

	checkRecordMechanicValue: (
		relayTypeId: string,
		relayId: string,
		relayOneId: string,
		recordId: string
	) => Promise<{ dublicate: boolean }>

	checkRecordElectricValue: (
		relayTypeId: string,
		relayId: string,
		relayOneId: string,
		recordId: string
	) => Promise<{ dublicate: boolean }>

	getRecordsJournal: (
		page: number,
		relayTypeId?: string
	) => Promise<JournalRecordsResponse>

	updateMechanic: (mechanic: ElectricMechanic) => void
	updateElectric: (electric: ElectricMechanic) => void

	deleteRelayType: (relayTypeId: string) => void
	deleteRelay: (relayTypeId: string, relayId: string) => void
	// deleteRelayOne: (relayTypeId: string, relayId: string) => void
	deleteMechanic: (relayTypeId: string, relayId: string) => void
	deleteElectric: (relayTypeId: string, relayId: string) => void

	clearAll: () => void
}

export const relayStore = create<RelayState>((set) => ({
	relayTypes: [],
	relayOnes: [],
	fetchedRelayOneRelayIds: [],

	setDataFromUpdate: ({ type, data, action }) => {
		// The server compacts the update log: when a row changes, its earlier
		// entries are flagged isDeleted, so a client syncing from scratch can
		// receive an 'update' for something it has never seen. And IndexedDB is
		// replayed as 'create' on top of whatever sync already applied. Both
		// create and update therefore have to be upserts — a 'create' that
		// prepends blindly duplicates header columns, and an 'update' that only
		// maps over existing ids silently drops rows.
		if (type === 'relayType') {
			if (action === 'delete') {
				set((state) => ({
					relayTypes: state.relayTypes.filter(
						(rt) => rt.id !== data.id
					)
				}))
				return
			}
			set((state) => {
				const known = state.relayTypes.some(
					(rt) => rt.id === data.id
				)
				if (!known) {
					return {
						relayTypes: [
							{
								...data,
								relays: [],
								mechanics: [],
								electrics: []
							},
							...state.relayTypes
						]
					}
				}
				return {
					relayTypes: state.relayTypes.map((rt) =>
						rt.id === data.id
							? {
									// The payload is the bare RelayType row and
									// carries no children — keeping the spread
									// order here is what stops relays/mechanics/
									// electrics from becoming undefined.
									...rt,
									...data,
									relays: rt.relays ?? [],
									mechanics: rt.mechanics ?? [],
									electrics: rt.electrics ?? []
								}
							: rt
					)
				}
			})
			return
		}

		if (type === 'relayOne') {
			if (action === 'delete') {
				set((state) => ({
					relayOnes: state.relayOnes.filter(
						(ro) => ro.id !== data.id
					)
				}))
				return
			}
			set((state) => ({
				relayOnes: state.relayOnes.some((ro) => ro.id === data.id)
					? state.relayOnes.map((ro) =>
							ro.id === data.id ? { ...ro, ...data } : ro
						)
					: [data, ...state.relayOnes]
			}))
			return
		}

		const key =
			type === 'relay'
				? 'relays'
				: type === 'mechanic'
					? 'mechanics'
					: 'electrics'

		set((state) => ({
			relayTypes: state.relayTypes.map((rt) => {
				if (rt.id !== data.relayTypeId) return rt
				// relays/mechanics/electrics hold different shapes; only `id`
				// matters here, so treat them uniformly.
				const current = (rt[key] ?? []) as { id: string }[]
				const incoming = data as unknown as { id: string }

				if (action === 'delete') {
					return {
						...rt,
						[key]: current.filter(
							(item) => item.id !== incoming.id
						)
					}
				}

				const known = current.some(
					(item) => item.id === incoming.id
				)
				return {
					...rt,
					[key]: known
						? current.map((item) =>
								item.id === incoming.id
									? { ...item, ...incoming }
									: item
							)
						: [incoming, ...current]
				}
			})
		}))
	},

	addRelayType: async () => {
		set((state) => ({
			relayTypes: [
				{
					id: Math.random().toString(),
					createdAt: new Date().toISOString(),
					name: 'Название',
					relays: [],
					mechanics: [],
					electrics: [],
					isNew: true
				},
				...state.relayTypes
			]
		}))
	},
	addRelay: async (relayTypeId) => {
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						relays: [
							{
								id: Math.random().toString(),
								createdAt: new Date().toISOString(),
								name: 'Название',
								relayTypeId,
								relayUnique: '0',
								madeYear: 2025,
								expiration: '5 лет',
								relayOne: [],
								isNew: true
							},
							...relayType.relays
						]
					}
				}
				return relayType
			})
		}))
	},
	addRelayOne: async (relayTypeId, relayId) => {
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						relays: [
							{
								id: Math.random().toString(),
								createdAt: new Date().toISOString(),
								name: 'Название',
								relayTypeId,
								relayId,
								expiration: '5 лет',
								relayOne: [],
								isNew: true
							},
							...relayType.relays
						]
					}
				}
				return relayType
			})
		}))
	},
	addMechanic: async (relayTypeId) => {
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						mechanics: [
							{
								id: Math.random().toString(),
								createdAt: new Date().toISOString(),
								name: 'Название',
								relayTypeId,
								relayId: '0',
								order: 0,
								isAcceptsValue: false,
								orderInLevel: 0,
								colspan: 1,
								rowspan: 3,
								level: 0,
								vertical: false,
								measurement: 'метр',
								parentId: null,
								isNew: true
							},
							...relayType.mechanics
						]
					}
				}
				return relayType
			})
		}))
	},
	addElectric: async (relayTypeId) => {
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						electrics: [
							{
								id: Math.random().toString(),
								createdAt: new Date().toISOString(),
								name: 'Название',
								relayTypeId,
								order: 0,
								isAcceptsValue: false,
								orderInLevel: 0,
								colspan: 1,
								rowspan: 3,
								level: 0,
								vertical: false,
								measurement: 'метр',
								parentId: null,
								isNew: true
							},
							...relayType.electrics
						]
					}
				}
				return relayType
			})
		}))
	},

	createRelayType: async (relayType) => {
		if (
			relayStore
				.getState()
				.relayTypes.find(
					(rt) => rt.id === relayType.id && !rt.isNew
				)
		) {
			set((state) => ({
				relayTypes: state.relayTypes.map((rt) => {
					if (rt.id === relayType.id) {
						return relayType
					}
					return rt
				})
			}))
			return await updateRelayType(relayType)
		}
		const data = await createRelayType(relayType)

		set((state) => ({
			...state,
			relayTypes: [
				// data,
				...state.relayTypes.filter(
					(rt) => rt.id !== relayType.id && relayType.id !== data.id
				)
			]
		}))
	},
	createRelay: async (relay) => {
		if (
			relayStore
				.getState()
				.relayTypes.find((rt) => rt.id === relay.relayTypeId)
				?.relays.find((r) => r.id === relay.id && !r.isNew)
		) {
			// 	set((state) => ({
			// 		...state,
			// 		relayTypes: state.relayTypes.map((rt) => {
			// 			if (rt.id === relay.relayTypeId) {
			// 				return {
			// 					...rt,
			// 					relays: rt.relays.map((r) => {
			// 						if (r.id === relay.id) {
			// 							return relay
			// 						}
			// 						return r
			// 					})
			// 				}
			// 			}
			// 			return rt
			// 		})
			// 	}))

			return await updateRelay(relay)
		}
		// const data =
		await createRelay(relay)
		// set((state) => ({
		// 	...state,
		// 	relayTypes: state.relayTypes.map((rt) => {
		// 		if (rt.id === relay.relayTypeId) {
		// 			return {
		// 				...rt,
		// 				relays: [
		// 					data,
		// 					...rt.relays.filter(
		// 						(r) => r.id !== relay.id
		// 					)
		// 				]
		// 			}
		// 		}
		// 		return rt
		// 	})
		// }))
	},

	createRelayOne: async (relay) => {
		const data = await createRelayOne(relay)

		set((state) => ({
			...state,
			relayOnes: [data, ...state.relayOnes]
		}))
		return data
	},
	createMechanic: async (mechanic) => {
		if (
			relayStore
				.getState()
				.relayTypes.find(
					(rt) => rt.id === mechanic.relayTypeId
				)
				?.mechanics.find(
					(m) => m.id === mechanic.id && !m.isNew
				)
		) {
			set((state) => ({
				...state,
				relayTypes: state.relayTypes.map((rt) => {
					if (rt.id === mechanic.relayTypeId) {
						return {
							...rt,
							mechanics: rt.mechanics.map((m) => {
								if (m.id === mechanic.id) {
									return mechanic
								}
								return m
							})
						}
					}
					return rt
				})
			}))
			return await updateMechanic(mechanic)
		}
		const data = await createMechanic(mechanic)

		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((rt) => {
				if (rt.id === mechanic.relayTypeId) {
					return {
						...rt,
						mechanics: [
							// data,
							...rt.mechanics.filter(
								(m) => m.id !== mechanic.id && mechanic.id !== data.id
							)
						]
					}
				}
				return rt
			})
		}))
	},
	createElectric: async (electric) => {
		if (
			relayStore
				.getState()
				.relayTypes.find(
					(rt) => rt.id === electric.relayTypeId
				)
				?.electrics.find(
					(e) => e.id === electric.id && !e.isNew
				)
		) {
			set((state) => ({
				...state,
				relayTypes: state.relayTypes.map((rt) => {
					if (rt.id === electric.relayTypeId) {
						return {
							...rt,
							electrics: rt.electrics.map((e) => {
								if (e.id === electric.id) {
									return electric
								}
								return e
							})
						}
					}
					return rt
				})
			}))
			return await updateElectric(electric)
		}
		const data = await createElectric(electric)

		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((rt) => {
				if (rt.id === electric.relayTypeId) {
					return {
						...rt,
						electrics: [
							// data,
							...rt.electrics.filter(
								(e) => e.id !== electric.id && electric.id !== data.id
							)
						]
					}
				}
				return rt
			})
		}))
	},

	createRecord: async (record) => {
		const data = await createRecord(record)
		return data as Record
	},

	createMechanicValue: async (mechanicValue) => {
		const data = await createMechanicValue(mechanicValue)
		return data
	},

	createElectricValue: async (electricValue) => {
		const data = await createElectricValue(electricValue)
		return data
	},

	fetchRelayTypes: async () => {
		const relayTypes = await fetchRelayTypes()

		set(() => ({
			relayTypes: relayTypes.map((relayType) => ({
				...relayType,
				relays: [],
				mechanics: [],
				electrics: [],
				isRelaysFetched: false
			}))
		}))
	},

	fetchRelaysByType: async (relayTypeId) => {
		if (
			relayStore
				.getState()
				.relayTypes.find(
					(relayType: RelayType) =>
						relayType.id === relayTypeId
				)?.isRelaysFetched
		) {
			return
		}
		const relays = await fetchRelaysByType(relayTypeId)
		set((state) => {
			return {
				...state,
				relayTypes: state.relayTypes.map((relayType) => {
					if (relayType.id === relayTypeId) {
						return {
							...relayType,
							relays: relays.relays,
							isRelaysFetched: true
						}
					}
					return relayType
				})
			}
		})
	},
	fetchRelayOnesByRelayId: async (relayId) => {
		if (
			relayStore
				.getState()
				.fetchedRelayOneRelayIds.includes(relayId)
		) {
			return
		}

		const relayOnes = await fetchRelayOnesByRelayId(relayId)

		set((state) => {
			return {
				...state,
				relayOnes: [...state.relayOnes, ...relayOnes],
				fetchedRelayOneRelayIds: [
					...state.fetchedRelayOneRelayIds,
					relayId
				]
			}
		})
	},
	fetchRelayOneRecords: async (
		relayTypeId,
		relayId,
		relayOneId,
		page = 1
	) => {
		const records = await fetchRelayOneRecords(
			relayTypeId,
			relayId,
			relayOneId,
			page
		)

		return records
	},
	fetchRelayOneRecordOne(
		relayTypeId,
		relayId,
		relayOneId,
		recordId
	) {
		return fetchRelayOneRecordOne(
			relayTypeId,
			relayId,
			relayOneId,
			recordId
		)
	},
	fetchMechanicsByType: async (relayTypeId) => {
		if (
			relayStore
				.getState()
				.relayTypes.find(
					(relayType: RelayType) =>
						relayType.id === relayTypeId
				)?.isMechanicFetched
		) {
			return
		}

		const { mechanics } = await fetchMechanicByType(relayTypeId)
		set((state) => {
			return {
				...state,
				relayTypes: state.relayTypes.map((relayType) => {
					if (relayType.id === relayTypeId) {
						return {
							...relayType,
							mechanics,
							isMechanicFetched: true
						}
					}
					return relayType
				})
			}
		})
	},
	fetchElectricsByType: async (relayTypeId) => {
		if (
			relayStore
				.getState()
				.relayTypes.find(
					(relayType: RelayType) =>
						relayType.id === relayTypeId
				)?.isElectricFetched
		) {
			return
		}

		const electrics = await fetchElectricByType(relayTypeId)

		set((state) => {
			return {
				...state,
				relayTypes: state.relayTypes.map((relayType) => {
					if (relayType.id === relayTypeId) {
						return {
							...relayType,
							electrics: electrics.electrics,
							isElectricFetched: true
						}
					}
					return relayType
				})
			}
		})
	},

	fetchRelayOneMechanicValue: async (relayOneId) => {
		const data = await fetchRelayOneMechanicValue(relayOneId)

		return data
	},

	fetchRelayOneElectricValue: async (relayOneId) => {
		const data = await fetchRelayOneElectricValue(relayOneId)

		return data
	},

	checkRecordMechanicValue: async (
		relayTypeId,
		relayId,
		relayOneId,
		recordId
	) => {
		const data = await checkRecordMechanicValue(
			relayTypeId,
			relayId,
			relayOneId,
			recordId
		)

		return data
	},

	checkRecordElectricValue: async (
		relayTypeId,
		relayId,
		relayOneId,
		recordId
	) => {
		const data = await checkRecordElectricValue(
			relayTypeId,
			relayId,
			relayOneId,
			recordId
		)

		return data
	},

	getRecordsJournal: async (page, relayTypeId) => {
		const data = await getRecordsJournal(page, relayTypeId)
		return data
	},

	updateMechanic: async (mechanic) => {
		await updateMechanic(mechanic)
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((rt) => {
				if (rt.id === mechanic.relayTypeId) {
					return {
						...rt,
						mechanics: rt.mechanics.map((m) => {
							if (m.id === mechanic.id) {
								return mechanic
							}
							return m
						})
					}
				}
				return rt
			})
		}))
	},

	updateElectric: async (electric) => {
		await updateElectric(electric)
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((rt) => {
				if (rt.id === electric.relayTypeId) {
					return {
						...rt,
						electrics: rt.electrics.map((e) => {
							if (e.id === electric.id) {
								return electric
							}
							return e
						})
					}
				}
				return rt
			})
		}))
	},

	deleteRelayType: async (relayTypeId) => {
		await deleteRelayType(relayTypeId)
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.filter(
				(relayType) => relayType.id !== relayTypeId
			)
		}))
	},
	deleteRelay: async (relayTypeId, relayId) => {
		await deleteRelay(relayId)
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						relays: relayType.relays.filter(
							(relay) => relay.id !== relayId
						)
					}
				}
				return relayType
			})
		}))
	},
	deleteMechanic: async (relayTypeId, relayId) => {
		await deleteMechanic(relayId)
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						mechanics: relayType.mechanics.filter(
							(mechanic) => mechanic.id !== relayId
						)
					}
				}
				return relayType
			})
		}))
	},
	deleteElectric: async (relayTypeId, relayId) => {
		await deleteElectric(relayId)
		set((state) => ({
			...state,
			relayTypes: state.relayTypes.map((relayType) => {
				if (relayType.id === relayTypeId) {
					return {
						...relayType,
						electrics: relayType.electrics.filter(
							(electric) => electric.id !== relayId
						)
					}
				}
				return relayType
			})
		}))
	},

	clearAll: () =>
		set(() => ({
			relayTypes: [],
			relayOnes: [],
			fetchedRelayOneRelayIds: []
		}))
}))
